import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setTokens } from 'redux/auth/auth-slice';
import { AppDispatch, RootState } from 'redux/store';

type CompanyEntry = [number, string];

/**
 * SUPER_SUPER_ADMIN-only company switcher in the global header.
 *
 * Compact trigger shows the active company from `user.service_provider_id`.
 * Menu lists service providers; selecting one switches tenant and reloads.
 */
const CompanySwitcher: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const { service_provider_id: activeSpId } = useSelector(
        (state: RootState) => state.user.entity
    );

    const hasFetchedRef = useRef(false);
    const [companies, setCompanies] = useState<CompanyEntry[]>([]);
    const [switching, setSwitching] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    useEffect(() => {
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        (async () => {
            try {
                const response = await api.companies.getCompanies();
                const entries: CompanyEntry[] = Object.entries(response?.data ?? {}).map(
                    ([key, value]) => [parseInt(key, 10), String(value)]
                );
                setCompanies(entries);
            } catch (err) {
                console.error('Failed to load companies', err);
            }
        })();
    }, []);

    const selectedCompany = useMemo(() => {
        if (activeSpId == null || Number.isNaN(Number(activeSpId))) {
            return null;
        }
        return companies.find(([id]) => Number(id) === Number(activeSpId)) ?? null;
    }, [companies, activeSpId]);

    const selectedLabel = selectedCompany?.[1] || translate('selectCompany');

    const initials = useMemo(() => {
        const parts = selectedLabel.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }, [selectedLabel]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (switching) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSwitch = async (entry: CompanyEntry) => {
        if (switching) return;

        if (Number(activeSpId) === entry[0]) {
            handleClose();
            return;
        }

        setSwitching(true);
        handleClose();

        try {
            const resp = await api.serviceProvider.switchSP(entry[0]);
            if (resp.status === 200 || resp.status === 201) {
                dispatch(setTokens(resp.data));
                window.location.assign('/dashboard');
                return;
            }
        } catch (err) {
            console.error('Failed to switch service provider', err);
        }

        setSwitching(false);
    };

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={handleOpen}
                disabled={switching || !companies.length}
                aria-haspopup="menu"
                aria-expanded={open ? 'true' : undefined}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    maxWidth: { xs: 120, sm: 180, md: 200 },
                    height: 32,
                    m: 0,
                    px: 0.5,
                    py: 0,
                    border: 'none',
                    borderRadius: 2,
                    cursor: switching || !companies.length ? 'default' : 'pointer',
                    bgcolor: open ? 'action.selected' : 'transparent',
                    color: 'text.primary',
                    transition: 'background-color 0.15s ease',
                    opacity: switching || !companies.length ? 0.7 : 1,
                    '&:hover': {
                        bgcolor: 'action.hover',
                    },
                    '&:disabled': {
                        cursor: 'default',
                    },
                }}
            >
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1.25,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.selected',
                        color: 'primary.main',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                    }}
                >
                    {switching ? (
                        <CircularProgress size={12} sx={{ color: 'inherit' }} />
                    ) : (
                        initials
                    )}
                </Box>
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        letterSpacing: '-0.01em',
                        maxWidth: { xs: 56, sm: 110, md: 140 },
                        display: { xs: 'none', sm: 'block' },
                        color: 'text.primary',
                    }}
                >
                    {selectedLabel}
                </Typography>
                <KeyboardArrowDownRoundedIcon
                    sx={{
                        fontSize: 18,
                        color: 'text.secondary',
                        flexShrink: 0,
                        transition: 'transform 0.15s ease',
                        transform: open ? 'rotate(180deg)' : 'none',
                    }}
                />
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            mt: 0.75,
                            minWidth: 220,
                            maxWidth: 300,
                            maxHeight: 360,
                            py: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            boxShadow: 3,
                        },
                    },
                }}
            >
                {companies.map((entry) => {
                    const [id, name] = entry;
                    const isSelected = Number(id) === Number(activeSpId);
                    return (
                        <MenuItem
                            key={id}
                            selected={isSelected}
                            onClick={() => handleSwitch(entry)}
                            dense
                            sx={{ mx: 0.5, px: 1.25, py: 0.75, borderRadius: 1.5, gap: 1 }}
                        >
                            <Typography
                                noWrap
                                sx={{
                                    flex: 1,
                                    fontSize: 13,
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? 'primary.main' : 'text.primary',
                                }}
                            >
                                {name}
                            </Typography>
                            {isSelected && (
                                <CheckRoundedIcon
                                    sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default CompanySwitcher;
